import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { Field, FieldArray } from 'redux-form';
import { t } from '../../../modules/infra/i18n';

import config from '../../../config';
import {
  generateTimes,
  checkDate,
  checkPresentDays,
} from '../helpers/schedules';
import { getUTCStartOfDay } from '../helpers/times';
import Icon, { GLYPHS } from '../icon';
import ToolTip from '../tooltip';
import DatePicker from '../DatePickerV2';
import withDateFormat from '../../auth/utils/withDateFormat';
import WorkDayConflictIconWithTooltip, { WorkDayConflictTooltip } from './workDayConflictIconWithTooltip';
import { getDayOnConflictTooltip } from './getScheduleConflictTooltip';

const renderSelectTimes = (time, index) => (
  <option
    value={time}
    key={index}
  >
    { time }
  </option>
);

class Days extends Component {
  constructor(props) {
    super(props);
    this.fieldsInput = this.setupFieldsInput();
    this.dayInput = this.setupDayInput();
  }

  setupDayOnCalendarWithTooltips() {
    const { getCalendarDayWithConflicts } = this.props;

    if (!getCalendarDayWithConflicts) {
      return null;
    }

    return (calendarDay) => {
      const isBeforeToday = moment(calendarDay, config.dateFormatISO)
        .isBefore(moment(new Date())
          .startOf('day'), 'day');

      const calendarDayWithConflicts = getCalendarDayWithConflicts(calendarDay);
      const hasConflicts = calendarDayWithConflicts.conflicts && calendarDayWithConflicts.conflicts.length;

      if (isBeforeToday || !hasConflicts) {
        return (<span>{calendarDay.getDate()}</span>);
      }

      const tooltip = getDayOnConflictTooltip(calendarDayWithConflicts.conflicts);

      return (
        <div className="c-info-tooltip-new-handler">
          {calendarDayWithConflicts.day.getDate()}
          <WorkDayConflictTooltip
            tooltipHeader={tooltip.title}
            tooltipText={tooltip.text}
          />
        </div>
      );
    };
  }

  setupDayInput() {
    const {
      timezone,
      type,
      canModifyDays,
    } = this.props;

    return ({ input, times, rowName, onClick, conflicts }) => {
      const date = moment(input.value, config.dateFormatISO);
      const isLoading = this.props.isInLoadingList(date.format(config.dateFormatISO));
      const dayClass = classNames('c-days-list__item', {
        'is-hidden': date.isBefore(moment.tz(timezone).format(config.dateFormatISO), 'day'), // Old days
        'no-pointer-events': !canModifyDays,
        'u-opacity': isLoading,
      });
      const handleClick = () => {
        onClick();
      };

      return (
        <div className={dayClass} data-automation-id={`day-${type}-item-${date.format(config.dateFormatISO)}`}>
          <div className={classNames('c-radio__label u-pl0', { 'is-disabled': !canModifyDays })}>
            { date.format(this.props.dateFormat) }
          </div>
          <div>
            <WorkDayConflictIconWithTooltip
              className="u-ml--"
              dayConflicts={conflicts}
              type="dayOnOff"
              display-if={conflicts && conflicts.length}
            />
          </div>
          <div
            className="u-text-right"
            display-if={type === 'on'}
          >
            <Field
              component="input"
              disabled={!canModifyDays}
              name={`${rowName}.id`}
              type="hidden"
            />
            <Field
              className="c-select c-select--small u-mr-- u-ml-"
              component="select"
              disabled={!canModifyDays}
              name={`${rowName}.startTime`}
            >
              { times }
            </Field>
            <Field
              className="c-select c-select--small"
              component="select"
              disabled={!canModifyDays}
              name={`${rowName}.endTime`}
            >
              { times }
            </Field>
          </div>
          <div>
            <Icon
              display-if={canModifyDays && !isLoading}
              glyph={GLYPHS.xTag}
              className="o-icon--pointer"
              onClick={handleClick}
            />
          </div>
        </div>
      );
    };
  }

  setupFieldsInput() {
    const {
      type,
      canModifyDays,
    } = this.props;

    return ({ fields, times, arePresentDays, days }) => {
      const selectTimes = times.map(renderSelectTimes);
      const title = type === 'on' ? t('common:schedule_form.days_on_title') : t('common:schedule_form.days_off_title');

      const list = fields.map((day, index) => (
        <Field
          name={`${day}.day`}
          rowName={day}
          component={this.dayInput}
          times={selectTimes}
          key={index}
          conflicts={!!days && !!days[index] && days[index].conflicts}
          onClick={() => this.props.removeDay(fields, index)}
        />
      ));

      this.addDay = (val, to) => {
        const add = (value) => {
          const date = getUTCStartOfDay({
            day: value,
          }).format();

          if (this.checkDate(date)) {
            return;
          }

          this.props.createDay(fields, value);
        };

        const start = moment(val);
        if (!to) {
          add(start.toDate());
          return;
        }

        /*
        * traverse the range from later to earlier dates to push later dates first
        * redux-form's FieldArray can't update fields property as fast so we can't rely on data in the fields variable
        * check out the thread
        * https://bitbucket.org/Dsalvant/squire-app/pull-requests/2358/sa-5147-insert-days-on-and-days-off-in/diff#comment-107753774
        */
        const end = moment(to);
        while (end.isSameOrAfter(start, 'day')) {
          add(end.toDate());
          end.subtract(1, 'day');
        }
      };

      const shouldRenderCustomDay = type === 'on' && !!this.props.getCalendarDayWithConflicts;
      const renderCustomDay = shouldRenderCustomDay ? this.setupDayOnCalendarWithTooltips() : null;

      return (
        <div>
          <div className="c-wrapper u-pv0">
            <span className="c-heading c-heading--lightgrey u-mb- u-pb0 u-pt0">{ title }</span>
            <ToolTip
              display-if={type === 'on'}
              text={t('common:schedule_form.days_on_tooltip')}
            />
          </div>
          { list.length && arePresentDays ? (
            <div
              className={classNames('c-wrapper u-pb0', {
                'c-days-on-list': type === 'on',
                'c-days-off-list': type === 'off',
              })}
            >
              {list}
            </div>
          ) : (
            <div className="c-wrapper u-pb0 zeta">{t('common:schedule_form.no_days_message', { type })}</div>
          )}
          <div
            display-if={canModifyDays}
            className="c-date-picker-days-off c-wrapper u-pv0"
          >
            <DatePicker
              onChange={this.addDay}
              startDate={new Date()}
              component="anchor"
              range={type === 'off'}
              anchorLabel={t('common:schedule_form.add_day_label', { type })}
              renderDay={renderCustomDay}
            />
          </div>
        </div>
      );
    };
  }

  checkDate(date) {
    const { days } = this.props;
    return checkDate(days, date);
  }

  render() {
    const { days, type, timezone } = this.props;
    const times = generateTimes(false);
    return (
      <FieldArray
        name={`days_${type}`}
        component={this.fieldsInput}
        days={days}
        times={times}
        arePresentDays={checkPresentDays(days, timezone)}
      />
    );
  }
}

Days.propTypes = {
  timezone: PropTypes.string.isRequired,
  days: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.string.isRequired,
  createDay: PropTypes.func.isRequired,
  removeDay: PropTypes.func.isRequired,
  isInLoadingList: PropTypes.func.isRequired,
  canModifyDays: PropTypes.bool.isRequired,
  getCalendarDayWithConflicts: PropTypes.func,
};

export default withDateFormat()(Days);
